import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import { BackfillDeal, MeResponse } from "../types";

/** Вкладка «Дозаполнить» (Batch6 §бэкафилл переходного периода).
 *
 *  Показывает заявки с пробелами в данных, внедрённых после их заведения:
 *  - missing_instruction_no — импорт без номера Поручения;
 *  - missing_counterparty   — импорт без контрагента;
 *  - pending_approval       — заявки задним числом, ждущие одобрения. */

const KIND_LABEL: Record<string, string> = {
  missing_instruction_no: "🔢 Без номера Поручения",
  missing_counterparty: "👤 Без контрагента",
  pending_approval: "⏳ Ожидают одобрения",
};

function fmt(n: number): string {
  return n.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
}

type Kind = "missing_instruction_no" | "missing_counterparty" | "pending_approval";

export function BackfillPage({ me }: { me: MeResponse }) {
  const [kind, setKind] = useState<Kind>("missing_instruction_no");
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [deals, setDeals] = useState<BackfillDeal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    api
      .get<Record<string, number>>("/api/backfill/summary")
      .then(setSummary)
      .catch(() => undefined);
  }, [reload]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ deals: BackfillDeal[]; kind: string }>(`/api/backfill?kind=${kind}`)
      .then((r) => setDeals(r.deals))
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }, [kind, reload]);

  if (!me.permissions.backfill_view) {
    return <div className="text-tg-hint text-sm">Раздел доступен админу и финруку.</div>;
  }

  const approve = async (dealId: number) => {
    // Только pending_approval: одобрить означает снять флаг.
    try {
      await api.post(`/api/backfill/approve/${dealId}`, {});
      setReload((x) => x + 1);
    } catch (e) {
      setError((e as ApiError).message);
    }
  };

  return (
    <div>
      <h1 className="text-xl font-semibold mb-3">📝 Дозаполнить</h1>

      <div className="flex gap-1 mb-3 text-xs flex-wrap">
        {(["missing_instruction_no", "missing_counterparty", "pending_approval"] as Kind[]).map((k) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            className={
              "px-3 py-1.5 rounded-lg flex items-center gap-1.5 " +
              (kind === k ? "bg-tg-button text-tg-buttonText font-medium" : "bg-tg-sectionBg")
            }
          >
            <span>{KIND_LABEL[k]}</span>
            {summary[k] != null && summary[k] > 0 && (
              <span className="px-1.5 rounded-full bg-tg-destructive text-white text-[10px] font-bold">
                {summary[k]}
              </span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}
      {loading && <div className="text-tg-hint text-sm animate-pulse">Загрузка…</div>}

      <div className="space-y-2">
        {deals.map((d) => (
          <div key={d.deal_id} className="bg-tg-sectionBg rounded-xl p-3">
            <div className="flex items-center justify-between">
              <div className="font-medium text-sm">№{d.deal_id} · {d.deal_date}</div>
              <span className="text-xs text-tg-hint">{d.deal_type}</span>
            </div>
            <div className="text-xs text-tg-hint mt-0.5">
              {fmt(d.amount)} {d.currency} · {d.operator_name || "—"}
              {d.counterparty && <> · {d.counterparty}</>}
            </div>
            {kind === "pending_approval" && (
              <button
                onClick={() => approve(d.deal_id)}
                className="mt-2 px-3 py-1.5 rounded-lg bg-tg-button text-tg-buttonText text-xs font-medium"
              >
                ✅ Одобрить
              </button>
            )}
          </div>
        ))}
        {!loading && deals.length === 0 && (
          <div className="text-tg-hint text-sm">Все заявки заполнены — ничего не требует внимания.</div>
        )}
      </div>
    </div>
  );
}
